// src/components/Charts/pie/AnalyticsPieCard.jsx
import React from 'react';
import * as echarts from 'echarts';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AppContext from '../../../contexts/app-context';
import AuthContext from '../../../contexts/auth-context';
import useAxios from '../../../hooks/useAxios';
import PieDetailsDialog from '../utils/PieDetailsDialog';
import { normalizeRows, sumTotal, aggregateTopN, toEchartData } from '../utils/analyticsPie';

// External legend rendered below the chart (responsive, wraps, no overlap)
function ExternalLegend({ items }) {
  if (!items?.length) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        flexWrap: 'wrap',
      }}
    >
      {items.map((it) => (
        <Box
          key={it.name}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            p: 1,
            transition: 'all 0.2s ease-in-out',
            width: 'fit-content',

          }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              bgcolor: it.itemStyle?.color || 'grey.400',
              border: '2px solid white',
              boxShadow: '0 0 4px rgba(0,0,0,0.15)',
              flex: '0 0 auto',
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.65rem',
            }}
            title={it.name}
          >
            {it.name}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}


export default function AnalyticsPieCard({
  chartId,
  endpoint,
  i18n = {
    seriesName: 'Series',
    centerLabel: 'Items',
    detailsButton: 'Details',
    dialogTitle: 'All Items',
    entityName: 'Item',
    noData: 'No data',
  },
  nameKeys = ['name'],
  valueKeys = ['count'],
  parseResponse,
  baseColors,
  othersColor = '#B0BEC5',
  othersLabel = 'Others',
  topN = null,
  topCoveragePercent = null,
  colorMap = null,
  hideDetails
}) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  // const [total, setTotal] = React.useState(0);
  const [dialogTotal, setDialogTotal] = React.useState(0);
  const [echartData, setEchartData] = React.useState([]);
  const [openAll, setOpenAll] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const { globalFilterQuery } = React.useContext(AppContext);
  const { token } = React.useContext(AuthContext);
  const chartRef = React.useRef(null);
  const { operation } = useAxios();
  const opRef = React.useRef(operation);
  React.useEffect(() => { opRef.current = operation; }, [operation]);

  const parseResponseRef = React.useRef(parseResponse);
  React.useEffect(() => { parseResponseRef.current = parseResponse; }, [parseResponse]);

  const lastKeyRef = React.useRef('');

  // Fetch data
  React.useEffect(() => {
    if (!token || !endpoint) return;

    const fetchKey = `${endpoint}|${globalFilterQuery}|${!!token}`;
    if (lastKeyRef.current === fetchKey) {
      return;
    }
    lastKeyRef.current = fetchKey;

    setIsLoading(true);

    opRef.current(
      {
        method: 'GET',
        url: endpoint + (globalFilterQuery || ''),
        headers: { Authorization: `token ${token}` },
      },
      (data) => {
        try {
          const { rows: rawRows = [], otherSum = 0 } =
            typeof parseResponseRef.current === 'function'
              ? (parseResponseRef.current(data) || {})
              : { rows: Array.isArray(data) ? data : [] };

          const normalized = normalizeRows(rawRows, nameKeys, valueKeys).map((r, i) => ({
            ...r,
            ...rawRows[i]
          }));

          const shouldAggregate = topN != null || topCoveragePercent != null;
          const aggregatedRows = shouldAggregate
            ? aggregateTopN(normalized, topN, othersLabel, otherSum, topCoveragePercent)
            : normalized;

          const finalRows = aggregatedRows.filter(row => row.name !== othersLabel);
          // const finalTotal = sumTotal(finalRows);
          const allItemsTotal = sumTotal(normalized) + (otherSum || 0);

          const eData = toEchartData(finalRows, baseColors, othersColor, othersLabel, colorMap);

          setRows(normalized);
          // setTotal(finalTotal);
          setDialogTotal(allItemsTotal);
          setEchartData(eData);
        } catch (e) {
          console.error('Error parsing analytics response:', e);
          setRows([]);
          // setTotal(0);
          setEchartData([]);
        } finally {
          setIsLoading(false);
        }
      },
      (err) => {
        console.error('Fetch failed:', err);
        setIsLoading(false);
        setRows([]);
        // setTotal(0);
        setEchartData([]);
      }
    );
    // eslint-disable-next-line
  }, [token, endpoint, globalFilterQuery]);

  // Render chart - SOLUTION 2: No artificial inflation, accurate tooltips
  // Render chart without distortion using ResizeObserver

  React.useEffect(() => {
    if (isLoading || echartData.length === 0) return;

    const el = document.getElementById(`${chartId}-canvas`);
    if (!el) return;

    if (!chartRef.current) {
      chartRef.current = echarts.init(el);
    }

    const chart = chartRef.current;

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const value = Number(params.value) || 0;
          return `
          ${params.marker}<strong>${params.name}</strong><br/>
          Count: ${value.toLocaleString()}<br/>
          Share: ${(dialogTotal ? (value / dialogTotal) * 100 : 0).toFixed(2)}%
        `;
        }
      },
      legend: { show: false },
      series: [
        {
          name: i18n.seriesName,
          type: 'pie',
          radius: ['50%', '80%'],
          minAngle: 0.1,
          padAngle: 3,
          label: { show: false },
          labelLine: { show: false },
          emphasis: { scale: true, scaleSize: 10 },
          data: echartData,
        },
      ],
    };

    chart.setOption(option);

    const ro = new ResizeObserver(() => {
      setTimeout(() => {
        chart.resize();
      }, 0);
    });

    ro.observe(el);

    return () => {
      ro.disconnect();
    };
  }, [isLoading, echartData, dialogTotal, i18n.seriesName, chartId]);

  // UI states
  if (isLoading) {
    return (
      <Box sx={ { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (echartData.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 260 }}>
        <InfoOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography sx={{ color: 'text.secondary' }}>{i18n.noData}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Donut chart area */}
      <Box sx={{ position: 'relative', height: 220 }}>
        <Box id={`${chartId}-canvas`} sx={{ height: '100%' }} />
        {/* Center overlay with total */}
        <Box
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 500, fontSize: 26, letterSpacing: '0.5px', fontFamily: 'Outfit' }}>
              {dialogTotal}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>
              {i18n.centerLabel}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* External legend below the chart */}
      <Box sx={{display: 'flex', justifyContent: 'center'}}>
       {hideDetails && <ExternalLegend items={echartData}/>}
      </Box>

      {/* Details button */}
      {!hideDetails && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<ListAltIcon fontSize="small" />}
          onClick={() => setOpenAll(true)}
          sx={{
            minWidth: 0,
            maxWidth: 200,
            marginTop: 1,
            textTransform: 'none',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            '&:hover': { color: 'black' }
          }}
          disabled={rows.length === 0}
        >
          {i18n.detailsButton}
        </Button>
      </Box>}

      {/* Full list dialog */}
      <PieDetailsDialog
        open={openAll}
        onClose={() => {
          setOpenAll(false)
          setQuery('')
        }}
        title={i18n.dialogTitle}
        dialogTotal={dialogTotal}
        query={query}
        setQuery={setQuery}
        rows={[...rows].sort((a, b) => b.value - a.value)}
        entityName={i18n.entityName}
      />
    </Box>
  );
}